import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  ArrowLeft,
  MessageSquare,
  CheckCircle2,
  XCircle,
  Send,
  BarChart3,
  List,
  Users,
  RefreshCw
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "react-hot-toast";

const TYPE_LABELS = {
  new_assignment: "New Assignment",
  journal_response: "Journal Reviewed",
  forum_post: "Forum Post",
  message_received: "Message Received",
  assignment_response: "Assignment Graded",
  test_sms: "Test SMS",
  checkin_reminder: "Check-In Reminder"
};

export default function SmsAdmin({ user }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [diFilter, setDiFilter] = useState("all");
  const [usersDiFilter, setUsersDiFilter] = useState("all");
  const [testPhone, setTestPhone] = useState("");
  const [testMessage, setTestMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [smsUsers, setSmsUsers] = useState([]);

  useEffect(() => {
    if (!user?.is_super_admin) {
      navigate(-1);
      return;
    }
    fetchData();
  }, [user]);

  useEffect(() => {
    if (activeTab === "logs") fetchLogs();
  }, [statusFilter, typeFilter, diFilter]);

  useEffect(() => {
    if (activeTab === "users") fetchUsers();
  }, [usersDiFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, logsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/sms-stats`),
        axios.get(`${API}/admin/sms-logs?limit=50`),
        axios.get(`${API}/admin/sms-users`)
      ]);
      setStats(statsRes.data);
      setLogs(logsRes.data);
      setSmsUsers(usersRes.data);
    } catch (error) {
      console.error("Error fetching SMS data:", error);
      toast.error("Failed to load SMS data");
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      const params = new URLSearchParams({ limit: "50" });
      if (statusFilter !== "all") params.append("status_filter", statusFilter);
      if (typeFilter !== "all") params.append("type_filter", typeFilter);
      if (diFilter !== "all") params.append("di_filter", diFilter);
      const res = await axios.get(`${API}/admin/sms-logs?${params}`);
      setLogs(res.data);
    } catch (error) {
      console.error("Error fetching logs:", error);
    }
  };

  const fetchUsers = async () => {
    try {
      const params = new URLSearchParams();
      if (usersDiFilter !== "all") params.append("di_filter", usersDiFilter);
      const res = await axios.get(`${API}/admin/sms-users?${params}`);
      setSmsUsers(res.data);
    } catch (error) {
      console.error("Error fetching SMS users:", error);
    }
  };

  const sendTestSms = async () => {
    if (!testPhone) {
      toast.error("Please enter a phone number");
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/test-sms`, {
        phone_number: testPhone,
        message: testMessage || "This is a test SMS from Discipleship Intensive."
      });
      if (res.data.success) {
        toast.success("Test SMS sent successfully!");
        setTestPhone("");
        setTestMessage("");
        fetchData();
      } else {
        toast.error(`SMS failed: ${res.data.error || "Unknown error"}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test SMS");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-slate-300 border-t-amber-500 rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-serif text-2xl md:text-3xl font-bold text-slate-900">
              SMS Notifications
            </h1>
            <p className="text-slate-600 mt-1">Manage and monitor SMS notifications</p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-slate-200 pb-2">
          {[
            { id: "overview", label: "Overview", icon: BarChart3 },
            { id: "logs", label: "SMS Logs", icon: List },
            { id: "users", label: "User Settings", icon: Users },
            { id: "test", label: "Send Test", icon: Send }
          ].map(tab => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? "default" : "ghost"}
              size="sm"
              className={activeTab === tab.id ? "bg-slate-800" : ""}
              onClick={() => setActiveTab(tab.id)}
            >
              <tab.icon className="w-4 h-4 mr-2" /> {tab.label}
            </Button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === "overview" && stats && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <MessageSquare className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold">{stats.total_sent}</p>
                    <p className="text-sm text-slate-500">Total SMS Sent</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <CheckCircle2 className="w-8 h-8 text-green-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold">{stats.successful}</p>
                    <p className="text-sm text-slate-500">Successful</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <XCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold">{stats.failed}</p>
                    <p className="text-sm text-slate-500">Failed</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <Users className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold">{stats.users_with_sms}</p>
                    <p className="text-sm text-slate-500">Users with SMS</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* SMS by Type */}
            {stats.sms_by_type && Object.keys(stats.sms_by_type).length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">SMS by Type</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(stats.sms_by_type).map(([type, count]) => (
                      <div key={type} className="flex items-center justify-between">
                        <span className="text-sm text-slate-700">
                          {TYPE_LABELS[type] || type}
                        </span>
                        <Badge variant="secondary">{count}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Logs Tab */}
        {activeTab === "logs" && (
          <div className="space-y-4">
            {/* Filters */}
            <div className="flex gap-3 flex-wrap">
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                className="border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
              >
                <option value="all">All Status</option>
                <option value="success">Successful</option>
                <option value="failed">Failed</option>
              </select>
              <select
                value={typeFilter}
                onChange={e => setTypeFilter(e.target.value)}
                className="border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
              >
                <option value="all">All Types</option>
                {Object.entries(TYPE_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
              <select
                data-testid="logs-di-filter"
                value={diFilter}
                onChange={e => setDiFilter(e.target.value)}
                className="border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
              >
                <option value="all">DI + Other Users</option>
                <option value="di">DI Students Only</option>
                <option value="other">Other Users Only</option>
              </select>
            </div>

            {/* Logs Table */}
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-100 bg-slate-50">
                        <th className="text-left p-3 font-medium text-slate-600">Date</th>
                        <th className="text-left p-3 font-medium text-slate-600">Recipient</th>
                        <th className="text-left p-3 font-medium text-slate-600">Phone</th>
                        <th className="text-left p-3 font-medium text-slate-600">Type</th>
                        <th className="text-left p-3 font-medium text-slate-600">Status</th>
                        <th className="text-left p-3 font-medium text-slate-600">Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="p-8 text-center text-slate-500">
                            No SMS logs found
                          </td>
                        </tr>
                      ) : (
                        logs.map(log => (
                          <tr key={log.id} className="border-b border-slate-50 hover:bg-slate-50">
                            <td className="p-3 text-slate-600 whitespace-nowrap">
                              {log.created_at ? format(new Date(log.created_at), "MMM d, h:mm a") : "-"}
                            </td>
                            <td className="p-3 text-slate-900">
                              <div className="flex items-center gap-1.5">
                                {log.user_name}
                                {log.is_intensive_student && (
                                  <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100 text-[10px] px-1.5 py-0">DI</Badge>
                                )}
                              </div>
                            </td>
                            <td className="p-3 text-slate-600 font-mono text-xs">{log.phone_number}</td>
                            <td className="p-3">
                              <Badge variant="outline" className="text-xs">
                                {TYPE_LABELS[log.message_type] || log.message_type}
                              </Badge>
                            </td>
                            <td className="p-3">
                              {log.success ? (
                                <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                                  <CheckCircle2 className="w-3 h-3 mr-1" /> Sent
                                </Badge>
                              ) : (
                                <Badge className="bg-red-100 text-red-700 hover:bg-red-100">
                                  <XCircle className="w-3 h-3 mr-1" /> Failed
                                </Badge>
                              )}
                            </td>
                            <td className="p-3 text-slate-500 text-xs max-w-[200px] truncate">
                              {log.message_body || log.error || "-"}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === "users" && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-3 flex-wrap">
              <CardTitle className="text-lg">User SMS Settings</CardTitle>
              <select
                data-testid="users-di-filter"
                value={usersDiFilter}
                onChange={e => setUsersDiFilter(e.target.value)}
                className="border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
              >
                <option value="all">DI + Other Users</option>
                <option value="di">DI Students Only</option>
                <option value="other">Other Users Only</option>
              </select>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50">
                      <th className="text-left p-3 font-medium text-slate-600">Name</th>
                      <th className="text-left p-3 font-medium text-slate-600">Role</th>
                      <th className="text-center p-3 font-medium text-slate-600">DI Student</th>
                      <th className="text-left p-3 font-medium text-slate-600">Phone</th>
                      <th className="text-center p-3 font-medium text-slate-600">SMS</th>
                      <th className="text-center p-3 font-medium text-slate-600">Assignments</th>
                      <th className="text-center p-3 font-medium text-slate-600">Journals</th>
                      <th className="text-center p-3 font-medium text-slate-600">Forum</th>
                      <th className="text-center p-3 font-medium text-slate-600">Messages</th>
                      <th className="text-center p-3 font-medium text-slate-600">Graded</th>
                    </tr>
                  </thead>
                  <tbody>
                    {smsUsers.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="p-8 text-center text-slate-500">
                          No users found
                        </td>
                      </tr>
                    ) : (
                      smsUsers.map(u => {
                        const prefs = u.sms_preferences || {};
                        const Dot = ({ on }) => (
                          <span className={`inline-block w-2.5 h-2.5 rounded-full ${on ? "bg-green-500" : "bg-slate-200"}`} />
                        );
                        return (
                          <tr key={u.user_id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`sms-user-row-${u.user_id}`}>
                            <td className="p-3 text-slate-900 font-medium">{u.name || "—"}</td>
                            <td className="p-3">
                              <Badge variant="outline" className="capitalize text-xs">{u.role}</Badge>
                            </td>
                            <td className="p-3 text-center">
                              {u.is_intensive_student ? (
                                <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100 text-xs">DI</Badge>
                              ) : (
                                <span className="text-slate-300 text-xs">—</span>
                              )}
                            </td>
                            <td className="p-3 text-slate-600 font-mono text-xs">{u.phone_number || "—"}</td>
                            <td className="p-3 text-center">
                              {u.sms_notifications_enabled ? (
                                <Badge className="bg-green-100 text-green-700 hover:bg-green-100 text-xs">On</Badge>
                              ) : (
                                <Badge className="bg-slate-100 text-slate-500 hover:bg-slate-100 text-xs">Off</Badge>
                              )}
                            </td>
                            <td className="p-3 text-center"><Dot on={prefs.new_assignments} /></td>
                            <td className="p-3 text-center"><Dot on={prefs.journal_response} /></td>
                            <td className="p-3 text-center"><Dot on={prefs.forum_posts} /></td>
                            <td className="p-3 text-center"><Dot on={prefs.messages_received} /></td>
                            <td className="p-3 text-center"><Dot on={prefs.assignment_response} /></td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Test SMS Tab */}
        {activeTab === "test" && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Send Test SMS</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">
                  Phone Number (E.164 format)
                </label>
                <Input
                  value={testPhone}
                  onChange={e => setTestPhone(e.target.value)}
                  placeholder="+14177550200"
                  className="max-w-xs"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">
                  Message (optional)
                </label>
                <Input
                  value={testMessage}
                  onChange={e => setTestMessage(e.target.value)}
                  placeholder="This is a test SMS from Discipleship Intensive."
                  className="max-w-lg"
                />
              </div>
              <Button
                onClick={sendTestSms}
                disabled={sending || !testPhone}
                className="bg-slate-800 hover:bg-slate-900"
              >
                {sending ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" /> Send Test SMS
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
